import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, type Me } from '../lib/auth';
import { Banner, Button, ErrorBanner, Field, Input } from '../components/ui';

const GOOGLE_ERRORS: Record<string, string> = {
  google_state: 'The Google sign-in expired. Please try again.',
  google_token: 'Google did not complete the sign-in. Please try again.',
  google_account: 'That Google account cannot sign in here. Use the Google account with the same email as your portal account, or sign in with your email and password.',
  google_no_account: 'There is no portal account for that Google account. Use the Google account with the same email you were invited with, or ask Circdal to add you.',
};

export function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-art">
        <div className="ribs" />
        <img className="logo" src={`${import.meta.env.BASE_URL}brand/circdal-full-white.svg`} alt="Circdal" />
        <div>
          <h1>Every Circdal project, from first lead to installed wall.</h1>
          <p>Track projects, design in the configurator, request samples and send approved submittals from one place.</p>
        </div>
        <p className="small" style={{ color: '#86a597' }}>
          Recycled rPETG panels and screens, designed and printed in St. Louis.
        </p>
      </div>
      <div className="auth-form">
        <div className="auth-card">{children}</div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get('next') || '/';
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const config = useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<{ google: boolean; staffDomain: string; environmentLabel?: string | null }>('/auth/config') });

  if (user) return <Navigate to={next} replace />;

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ otpRequired: boolean; user?: Me }>('/auth/login', { email, password });
      if (res.otpRequired) {
        setStep('code');
        setNotice(`We emailed a 6-digit code to ${email}.`);
      } else if (res.user) {
        setUser(res.user);
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ user: Me }>('/auth/otp', { code });
      setUser(res.user);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const googleError = params.get('error');

  return (
    <AuthFrame>
      <div className="stack tight">
        <span className="eyebrow">Circdal rep portal</span>
        <h1>{step === 'password' ? 'Sign in' : 'Check your email'}</h1>
      </div>
      {googleError && GOOGLE_ERRORS[googleError] ? <Banner tone="error">{GOOGLE_ERRORS[googleError]}</Banner> : null}
      <ErrorBanner error={error} />
      {notice ? <Banner tone="info">{notice}</Banner> : null}
      {step === 'password' ? (
        <form className="stack" onSubmit={submitPassword}>
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </Field>
          <Field label="Password" htmlFor="password">
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" busy={busy}>
            Sign in
          </Button>
          <div className="row between small">
            <Link to="/forgot">Forgot your password?</Link>
          </div>
          {config.data?.google ? (
            <>
              <div className="row small muted" style={{ gap: 10, alignItems: 'center' }}>
                <hr className="divider" style={{ flex: 1 }} /> or <hr className="divider" style={{ flex: 1 }} />
              </div>
              <a className="btn" href="/api/auth/google/start">
                <GoogleMark /> Sign in with Google
              </a>
              <p className="tiny muted" style={{ margin: 0 }}>
                Use the Google account with the same email as your portal account. Google checks it, so there is no code to enter.
              </p>
            </>
          ) : null}
        </form>
      ) : (
        <form className="stack" onSubmit={submitCode}>
          <Field label="Sign-in code" htmlFor="code" hint="The code expires in 10 minutes.">
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              style={{ fontFamily: 'var(--font-mono)', fontSize: 20, letterSpacing: '0.3em' }}
            />
          </Field>
          <Button type="submit" variant="primary" busy={busy}>
            Verify and continue
          </Button>
          <Button
            variant="ghost"
            onClick={async () => {
              setError(null);
              try {
                await api.post('/auth/otp/resend');
                setNotice('We sent a new code.');
              } catch (err) {
                setError(err);
              }
            }}
          >
            Send a new code
          </Button>
        </form>
      )}
      <p className="tiny muted">Reps get an account from Circdal. Contact your Circdal partner manager if you need access.</p>
    </AuthFrame>
  );
}

/** Google's four-color G, drawn inline so the button needs no image file. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" style={{ flex: 'none' }}>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17.1z" />
      <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.8l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
    </svg>
  );
}
